/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, IconButton, FormControl, InputLabel, Select, MenuItem,
  Chip, CircularProgress, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import Swal from 'sweetalert2';
import { api } from '../../api';

const TEAL = '#00798E';

export default function ChangeDoctorDialog({ open, onClose, patient, onSuccess }) {
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [saving, setSaving] = useState(false);

  const currentDoctorUserId = patient?.contact?.doctor_maps?.[0]?.irm_users_id || '';
  const currentDoctorName = patient?.doctor_info?.doctor_name || 'Not assigned';
  const hasSpouse = Boolean(patient?.spouse?.contact);

  useEffect(() => {
    if (!open) return;
    setSelectedDoctorId(currentDoctorUserId || '');
    setLoadingDoctors(true);
    api.contacts.getDoctorsList()
      .then((res) => setDoctors(res?.data || []))
      .catch(() => setDoctors([]))
      .finally(() => setLoadingDoctors(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patient]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    if (!selectedDoctorId) return;

    if (String(selectedDoctorId) === String(currentDoctorUserId)) {
      Swal.fire({ icon: 'info', title: 'No Change', text: 'Selected doctor is already assigned to this patient.' });
      return;
    }

    const newDoctorName = doctors.find((d) => String(d.user_id) === String(selectedDoctorId))?.name || 'Selected doctor';

    const confirmText = hasSpouse
      ? `Doctor will be changed from "${currentDoctorName}" to "${newDoctorName}" for this patient AND their spouse. This action will be recorded in the audit log.`
      : `Doctor will be changed from "${currentDoctorName}" to "${newDoctorName}" for this patient. This action will be recorded in the audit log.`;

    const result = await Swal.fire({
      title: 'Change Doctor?',
      text: confirmText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: TEAL,
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, change doctor',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    try {
      const contactId = patient?.contact_id || patient?.contact?.id;
      await api.contacts.changeDoctor(contactId, selectedDoctorId);
      await Swal.fire({
        icon: 'success',
        title: 'Doctor Updated',
        text: hasSpouse ? 'Doctor changed successfully for the patient and spouse.' : 'Doctor changed successfully.',
        timer: 2200,
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Change Doctor',
        text: error.response?.data?.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${TEAL} 0%, #00acc1 100%)`,
          px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalHospitalIcon sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'white !important' }}>Change Doctor</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75) !important' }}>
                {patient?.name || 'Patient'} · {patient?.external_code || ''}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.8)' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '20px !important' }}>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#888', mb: 0.5 }}>Currently Assigned Doctor</Typography>
          <Chip label={currentDoctorName} size="small" sx={{ background: '#e0f7fa', color: '#00594f', fontWeight: 600 }} />
        </Box>

        {hasSpouse && (
          <Typography sx={{ fontSize: '0.72rem', color: '#e65100', mb: 2, background: '#fff3e0', p: 1, borderRadius: 1 }}>
            This patient has a linked spouse — the new doctor will be applied to both.
          </Typography>
        )}

        <FormControl fullWidth size="small">
          <InputLabel id="new-doctor-label">New Doctor</InputLabel>
          <Select
            labelId="new-doctor-label"
            label="New Doctor"
            value={selectedDoctorId}
            onChange={(e) => setSelectedDoctorId(e.target.value)}
            disabled={loadingDoctors || saving}
            sx={{ '& .MuiSelect-select': { minHeight: 40, display: 'flex', alignItems: 'center', boxSizing: 'border-box' } }}
          >
            {loadingDoctors && <MenuItem value=""><CircularProgress size={16} sx={{ mr: 1 }} />Loading...</MenuItem>}
            {!loadingDoctors && doctors.map((doctor) => (
              <MenuItem key={doctor.user_id} value={doctor.user_id}>{doctor.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ textTransform: 'none', color: '#555 !important' }}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving || loadingDoctors || !selectedDoctorId}
          variant="contained"
          sx={{ textTransform: 'none', background: TEAL, color: 'white !important', '&:hover': { background: '#006375' } }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
